import React from 'react';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import { SVG } from '../../constants/assetLists';
import AppMUIAutocomplete from './AppMUIAutoComplete';
import {
  BranchData,
  ClientDataType,
  SubBranchData,
} from '../../context/clients/ClientsContextProvider';

type TreeNode = ClientDataType | BranchData | SubBranchData;

type FlatNode = {
  id: string;
  name: string;
  icon: string;
  depth: number;
  hasChildren: boolean;
  isBranch?: boolean;
  isSubBranch?: boolean;
  raw: TreeNode;
};

const isSelectable = (o: FlatNode) => Boolean(o.isBranch || o.isSubBranch);
const isExpandOnly = (o: FlatNode) => o.hasChildren && !isSelectable(o);

// Returns the direct children of a node based on its type:
//   ClientDataType  → .branches (BranchData[])
//   BranchData      → .subBranches (SubBranchData[])
//   SubBranchData   → [] (leaf)
function getChildren(node: TreeNode): (BranchData | SubBranchData)[] {
  if (node.isClient) return (node as ClientDataType).branches || [];
  if (node.isBranch) return (node as BranchData).subBranches || [];
  return [];
}

function flattenVisibleTree(
  roots: (BranchData | ClientDataType)[],
  expandedSet: Set<string>,
): FlatNode[] {
  const out: FlatNode[] = [];

  const walk = (node: TreeNode, depth: number) => {
    const children = getChildren(node);
    const hasChildren = children.length > 0;

    out.push({
      id: node.id,
      name: node.name,
      icon: node.icon,
      depth,
      hasChildren,
      isBranch: node.isBranch,
      isSubBranch: node.isSubBranch,
      raw: node,
    });

    if (hasChildren && expandedSet.has(node.id)) {
      children.forEach((child) => walk(child, depth + 1));
    }
  };

  roots.forEach((root) => walk(root, 0));
  return out;
}

// Full tree map so selected items don't disappear when their parent is collapsed
function buildIdToFlatMap(
  roots: (BranchData | ClientDataType)[],
): Map<string, FlatNode> {
  const map = new Map<string, FlatNode>();

  const walk = (node: TreeNode, depth: number) => {
    const children = getChildren(node);
    const hasChildren = children.length > 0;

    map.set(node.id, {
      id: node.id,
      name: node.name,
      icon: node.icon,
      depth,
      hasChildren,
      isBranch: node.isBranch,
      isSubBranch: node.isSubBranch,
      raw: node,
    });

    children.forEach((child) => walk(child, depth + 1));
  };

  roots.forEach((r) => walk(r, 0));
  return map;
}

type Props = {
  allClients: (BranchData | ClientDataType)[];
  value: string[];
  onChange: (nodes: TreeNode[]) => void;
  label?: string;
  showClientChip?: boolean;
  // when showClientChip is true, selected value tags are hidden by default
  // (existing behavior, unchanged); set this to also render the selected
  // branch/subBranch chips alongside the client chip
  showSelectedTags?: boolean;
  required?: boolean;
  disablePortal?: boolean;
};

export type SubBranchNode = TreeNode & {
  isSubBranch: true;
  branchId: number;
};

const BranchTreeAutocomplete: React.FC<Props> = ({
  allClients,
  value,
  onChange,
  label = 'סניף',
  showClientChip = false,
  showSelectedTags = false,
  required = false,
  disablePortal,
}) => {
  const [expandedSet, setExpandedSet] = React.useState<Set<string>>(
    () => new Set(),
  );

  const flatOptions = React.useMemo(
    () => flattenVisibleTree(allClients, expandedSet),
    [allClients, expandedSet],
  );

  // ✅ NEW: build full tree lookup once per allClients change
  const idToFlat = React.useMemo(
    () => buildIdToFlatMap(allClients),
    [allClients],
  );

  // ✅ FIX: selected value must come from full tree, not only visible options
  const selectedFlat = React.useMemo(() => {
    return (value ?? [])
      .map((id) => idToFlat.get(id))
      .filter(Boolean) as FlatNode[];
  }, [value, idToFlat]);

  const toggleExpand = (id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleChange = (_e: unknown, newOptions: FlatNode[]) => {
    // Branches and sub-branches are selectable; clients are expand/collapse headers only.
    const selectableOptions = newOptions.filter(isSelectable);

    const idToNode = new Map<string, TreeNode>();
    const fillMap = (nodes: TreeNode[]) => {
      nodes.forEach((n) => {
        idToNode.set(n.id, n);
        getChildren(n).forEach((child) => fillMap([child]));
      });
    };
    fillMap(allClients);

    const mapped = selectableOptions
      .map((o) => idToNode.get(o.id))
      .filter(Boolean) as TreeNode[];

    onChange(mapped);
  };

  return (
    <AppMUIAutocomplete
      {...(disablePortal !== undefined && { disablePortal })}
      label={label}
      multiple
      fullWidth
      size="small"
      disableCloseOnSelect
      disableClearable
      required={required}
      options={flatOptions}
      value={selectedFlat}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      onChange={handleChange}
      sx={{
        '& + .MuiAutocomplete-popper .MuiAutocomplete-listbox > li[data-client="true"]:hover':
          {
            backgroundColor: 'transparent',
          },
        '& + .MuiAutocomplete-popper .MuiAutocomplete-listbox > li[data-client="true"].Mui-focused':
          {
            backgroundColor: 'transparent',
          },
      }}
      startAdornment={
        showClientChip
          ? allClients
              .filter((client) => client.isClient)
              .map((client) => (
                <Chip
                  key={client.id}
                  label={client.name}
                  size="small"
                  sx={{ marginInlineEnd: 1 }}
                />
              ))
          : undefined
      }
      {...(showClientChip && !showSelectedTags && { renderValue: () => null })}
      renderOption={(props, option, { selected }) => {
        const { key, ...restProps } = props;
        const paddingInline = Math.min(12 + option.depth * 16, 56);

        const expanded = expandedSet.has(option.id);
        const caretIcon = expanded ? SVG.dropdownIcon : SVG.dropUpIcon;

        const handleCaretClick: React.MouseEventHandler<HTMLButtonElement> = (
          e,
        ) => {
          e.stopPropagation(); // don't toggle checkbox / selection
          toggleExpand(option.id);
        };

        const liProps = isExpandOnly(option)
          ? {
              ...restProps,
              onClick: (e: React.MouseEvent<HTMLLIElement>) => {
                e.stopPropagation();
                toggleExpand(option.id);
              },
              'aria-selected': false as const,
            }
          : restProps;

        return (
          <li
            key={key}
            {...liProps}
            className={`mx-3 my-3 ${isExpandOnly(option) ? 'cursor-default' : 'cursor-pointer'}`}
            {...(option.raw.isClient && { 'data-client': 'true' })}
          >
            <div
              className={`w-full ${isSelectable(option) ? 'hover:bg-hoverBg' : ''} rounded-md px-[6px] py-2 transition-colors duration-200`}
              style={{
                paddingInlineStart: `${paddingInline}px`,
                paddingInlineEnd: '8px',
              }}
            >
              <div className="flex items-center gap-3">
                {option.hasChildren && (
                  <button
                    type="button"
                    className="w-[15px] h-[10px]"
                    onClick={handleCaretClick}
                  >
                    <img
                      src={caretIcon}
                      alt="caret"
                      className="w-full h-full object-contain"
                    />
                  </button>
                )}

                {isSelectable(option) && (
                  <Checkbox
                    checked={selected}
                    size="medium"
                    color="secondary"
                    sx={{ p: 0 }}
                  />
                )}

                <div className="flex items-center gap-2">
                  <div className="w-[20px] h-[20px]">
                    <img
                      src={
                        option.depth === 0
                          ? SVG.clientIcon
                          : option.depth === 1
                            ? SVG.filterSidebarRestaurantIcon
                            : SVG.taskIcon
                      }
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>

                  <span className="text-black whitespace-nowrap">
                    {option.name}
                  </span>
                </div>
              </div>
            </div>
          </li>
        );
      }}
    />
  );
};

export default BranchTreeAutocomplete;
